import { useEffect, useState } from "react";
import adminApi from "../lib/adminApi";
import { toast } from "sonner";
import { Plus, Trash, CheckCircle, Pencil, Lightning, X } from "@phosphor-icons/react";

const PROVIDER_OPTIONS = [
  { value: "openai", label: "OpenAI", models: ["gpt-5.2", "gpt-5.1", "gpt-5-mini", "gpt-4.1", "o3"] },
  { value: "anthropic", label: "Anthropic", models: ["claude-sonnet-4-6", "claude-opus-4-6", "claude-sonnet-4-5-20250929", "claude-haiku-4-5-20251001"] },
  { value: "gemini", label: "Google Gemini", models: ["gemini-3.1-pro-preview", "gemini-3-flash-preview", "gemini-2.5-pro", "gemini-2.5-flash"] },
  { value: "custom", label: "Endpoint custom (OpenAI-compatible · vLLM / Ollama / Groq / Together / Mistral)", models: [] },
];

const EMPTY = {
  provider: "openai",
  model: "gpt-5.2",
  api_key: "",
  base_url: "",
  label: "",
  is_active: false,
};

export default function AdminLLM() {
  const [providers, setProviders] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [fallback, setFallback] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [testing, setTesting] = useState({});

  const reload = async () => {
    const r = await adminApi.get("/llm/providers");
    setProviders(r.data.providers || []);
    setActiveId(r.data.active_provider_id);
    setFallback(r.data.fallback_enabled);
  };

  useEffect(() => {
    reload().catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const payload = { ...form };
        if (!payload.api_key) delete payload.api_key;
        await adminApi.patch(`/llm/providers/${editingId}`, payload);
        toast.success("Modèle mis à jour");
      } else {
        await adminApi.post("/llm/providers", form);
        toast.success("Modèle ajouté");
      }
      setForm(EMPTY);
      setEditingId(null);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce modèle ?")) return;
    await adminApi.delete(`/llm/providers/${id}`);
    toast.success("Modèle supprimé");
    await reload();
  };

  const activate = async (id) => {
    await adminApi.post(`/llm/providers/${id}/activate`);
    toast.success("Modèle activé");
    await reload();
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      provider: p.provider,
      model: p.model,
      api_key: "",
      base_url: p.base_url || "",
      label: p.label || "",
      is_active: p.is_active,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const toggleFallback = async () => {
    const next = !fallback;
    setFallback(next);
    await adminApi.patch("/llm/settings", { fallback_enabled: next });
    toast.success(`Fallback Emergent ${next ? "activé" : "désactivé"}`);
  };

  const testProvider = async (id) => {
    setTesting((t) => ({ ...t, [id]: true }));
    try {
      const r = await adminApi.post(`/llm/providers/${id}/test`, { prompt: "Réponds par le mot OK uniquement." });
      if (r.data.status === "ok") {
        toast.success(`Test réussi · ${r.data.response.slice(0, 80)}`);
      } else {
        toast.error(`Test échoué · ${r.data.error}`);
      }
    } finally {
      setTesting((t) => ({ ...t, [id]: false }));
    }
  };

  const currentProviderModels = PROVIDER_OPTIONS.find((p) => p.value === form.provider)?.models || [];

  return (
    <div data-testid="admin-llm-page">
      <div className="label-mono">§ 02 · Modèles IA</div>
      <h1 className="font-serif text-4xl sm:text-5xl font-medium mt-2">Configuration multi-fournisseurs</h1>
      <p className="text-[var(--muted)] mt-3 max-w-2xl">
        Définissez les clés API OpenAI, Anthropic, Google ou un endpoint custom. Le modèle actif est utilisé pour toutes les générations IA de la plateforme.
        En cas d'échec, la <strong>clé Emergent universelle</strong> sert de filet de sécurité automatique.
      </p>

      {/* Fallback toggle */}
      <div className="mt-8 flex items-center justify-between border border-black p-4 bg-[var(--paper)]" data-testid="fallback-toggle-row">
        <div>
          <div className="label-mono">§ Filet de sécurité</div>
          <div className="mt-1 text-sm">
            Si le fournisseur actif échoue, basculer automatiquement sur <strong>EMERGENT_LLM_KEY</strong> (Claude Sonnet 4.5).
          </div>
        </div>
        <button
          type="button"
          onClick={toggleFallback}
          data-testid="fallback-toggle"
          className={`label-mono px-5 py-2 border-2 ${fallback ? "bg-[var(--signal)] text-[var(--paper)] border-[var(--signal)]" : "bg-transparent border-black"}`}
        >
          {fallback ? "Activé" : "Désactivé"}
        </button>
      </div>

      {/* Form */}
      <form onSubmit={submit} className="mt-10 border border-black p-6 bg-[var(--paper)]" data-testid="llm-form">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl font-medium">
            {editingId ? "Modifier un modèle" : "Ajouter un modèle"}
          </h2>
          {editingId && (
            <button type="button" onClick={cancelEdit} className="text-sm underline" data-testid="llm-cancel-edit">
              <X size={14} className="inline" /> Annuler
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <div className="label-mono mb-2">Fournisseur</div>
            <select
              value={form.provider}
              onChange={(e) => {
                const p = e.target.value;
                const ms = PROVIDER_OPTIONS.find((o) => o.value === p)?.models || [];
                setForm({ ...form, provider: p, model: ms[0] || "" });
              }}
              className="w-full border border-black px-4 py-3 bg-white"
              data-testid="llm-form-provider"
            >
              {PROVIDER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="label-mono mb-2">Modèle</div>
            {currentProviderModels.length ? (
              <select
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
                className="w-full border border-black px-4 py-3 bg-white"
                data-testid="llm-form-model"
              >
                {currentProviderModels.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                required
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
                className="w-full border border-black px-4 py-3 bg-transparent"
                placeholder="ex: my-llama-3.1-70b"
                data-testid="llm-form-model"
              />
            )}
          </label>
          <label className="block md:col-span-2">
            <div className="label-mono mb-2">
              Clé API {editingId && <span className="text-[var(--muted)] normal-case">(laisser vide pour conserver la clé existante)</span>}
            </div>
            <input
              type="password"
              value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              className="w-full border border-black px-4 py-3 font-mono text-xs"
              placeholder="sk-..."
              data-testid="llm-form-key"
            />
          </label>
          {form.provider === "custom" && (
            <label className="block md:col-span-2">
              <div className="label-mono mb-2">Base URL (custom OpenAI-compatible)</div>
              <input
                type="url"
                value={form.base_url}
                onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                className="w-full border border-black px-4 py-3 font-mono text-xs"
                placeholder="https://my-llm-gateway.example.com/v1"
                data-testid="llm-form-base-url"
                required
              />
            </label>
          )}
          <label className="block md:col-span-2">
            <div className="label-mono mb-2">Libellé (facultatif)</div>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              className="w-full border border-black px-4 py-3"
              placeholder="ex: OpenAI prod 2026"
              data-testid="llm-form-label"
            />
          </label>
          <label className="flex items-center gap-2 md:col-span-2">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              data-testid="llm-form-active"
            />
            <span className="text-sm">Activer ce modèle immédiatement (désactive les autres)</span>
          </label>
        </div>
        <button
          type="submit"
          className="mt-6 bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-6 py-3 label-mono flex items-center gap-2"
          data-testid="llm-form-submit"
        >
          <Plus size={14} weight="bold" /> {editingId ? "Mettre à jour" : "Ajouter le modèle"}
        </button>
      </form>

      {/* List */}
      <div className="mt-10">
        <h2 className="font-serif text-2xl font-medium mb-4">Modèles configurés</h2>
        {providers.length === 0 ? (
          <div className="border border-dashed border-black/30 p-10 text-center text-[var(--muted)] italic" data-testid="llm-empty">
            Aucun modèle configuré. La plateforme utilise actuellement <strong>EMERGENT_LLM_KEY</strong> (Claude Sonnet 4.5).
          </div>
        ) : (
          <div className="space-y-3">
            {providers.map((p) => (
              <div
                key={p.id}
                className={`border-2 p-4 flex flex-col md:flex-row md:items-center gap-4 ${p.id === activeId ? "border-[var(--signal)] bg-[var(--signal)]/5" : "border-black"}`}
                data-testid={`llm-row-${p.id}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-serif text-xl font-medium">{p.label}</span>
                    {p.id === activeId && (
                      <span className="label-mono bg-[var(--signal)] text-[var(--paper)] px-2 py-0.5 text-[10px]">ACTIF</span>
                    )}
                  </div>
                  <div className="label-mono mt-1 text-[var(--muted)]">
                    {p.provider} · {p.model} · clé : {p.api_key_masked || "absente"}
                  </div>
                  {p.base_url && <div className="font-mono text-xs text-[var(--muted)] mt-1">URL · {p.base_url}</div>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {p.id !== activeId && (
                    <button
                      type="button"
                      onClick={() => activate(p.id)}
                      data-testid={`llm-activate-${p.id}`}
                      className="border border-black hover:bg-black hover:text-[var(--paper)] px-3 py-2 label-mono text-xs flex items-center gap-1"
                    >
                      <CheckCircle size={12} /> Activer
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => testProvider(p.id)}
                    disabled={testing[p.id]}
                    data-testid={`llm-test-${p.id}`}
                    className="border border-black hover:bg-black hover:text-[var(--paper)] px-3 py-2 label-mono text-xs flex items-center gap-1 disabled:opacity-50"
                  >
                    <Lightning size={12} /> {testing[p.id] ? "Test..." : "Tester"}
                  </button>
                  <button
                    type="button"
                    onClick={() => startEdit(p)}
                    data-testid={`llm-edit-${p.id}`}
                    className="border border-black hover:bg-black hover:text-[var(--paper)] px-3 py-2 label-mono text-xs flex items-center gap-1"
                  >
                    <Pencil size={12} /> Modifier
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(p.id)}
                    data-testid={`llm-delete-${p.id}`}
                    className="border border-[var(--signal)] text-[var(--signal)] hover:bg-[var(--signal)] hover:text-[var(--paper)] px-3 py-2 label-mono text-xs flex items-center gap-1"
                  >
                    <Trash size={12} /> Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
